import React, { useEffect, useMemo, useState } from "react";
import { Download, Trash2, X } from "lucide-react";
import { useDeletePdfMaterialMutation } from "../../../services/deletion-history/deletionHistoryApi";
import {
  useDeleteCourseLectureMutation,
  useUpdatePdfTitleMutation,
} from "../../../services/course/courseApi";
import { PdfTypeIcon, VideoTypeIcon } from "../../AdminIcons";

const CourseMaterialsModal = ({
  isOpen,
  item,
  courseId,
  materialType = "pdf",
  onClose,
}) => {
  const [deletePdf, { isLoading: isDeletingPdf }] =
    useDeletePdfMaterialMutation();
  const [deleteLecture, { isLoading: isDeletingLecture }] =
    useDeleteCourseLectureMutation();
  const [updatePdfTitle, { isLoading: isUpdatingTitle }] =
    useUpdatePdfTitleMutation();
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [localMaterials, setLocalMaterials] = useState([]);
  const [deletingMaterialId, setDeletingMaterialId] = useState(null);
  const materials = useMemo(() => {
    if (Array.isArray(item?.materials)) return item.materials;
    if (Array.isArray(item?.files)) return item.files;
    if (Array.isArray(item?.pdfFiles)) return item.pdfFiles;
    return [];
  }, [item]);

  useEffect(() => {
    setLocalMaterials(materials);
  }, [materials]);

  useEffect(() => {
    if (!isOpen) {
      setRenameTarget(null);
      setRenameValue("");
      setLocalMaterials([]);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleDelete = async (material) => {
    if (!courseId) return;

    const isRecorded = materialType === "recorded";
    const targetId = material?._id || material?.id || "";
    if (!targetId) return;

    try {
      setDeletingMaterialId(targetId);
      if (isRecorded) {
        await deleteLecture({ courseId, lectureId: targetId }).unwrap();
      } else {
        await deletePdf({ courseId, pdfId: targetId }).unwrap();
      }
      setLocalMaterials((currentMaterials) =>
        currentMaterials.filter((currentMaterial) => {
          const currentId = currentMaterial?._id || currentMaterial?.id || "";

          return currentId !== targetId;
        }),
      );
      if (typeof onClose === "function") {
        onClose();
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to delete material", err);
    }
  };

  const handleOpenRename = (material) => {
    const currentTitle = material?.title || "";
    setRenameTarget(material);
    setRenameValue(currentTitle);
  };

  const handleCloseRename = () => {
    setRenameTarget(null);
    setRenameValue("");
  };

  const getMaterialDownloadUrl = (material) =>
    material?.pdfMaterialInfo?.secureUrl ||
    material?.lectureInfo?.secureUrl ||
    material?.secureUrl ||
    material?.url ||
    "";

  const handleDownload = async (material) => {
    const downloadUrl = getMaterialDownloadUrl(material);
    const title = material?.title || "material";

    if (!downloadUrl) {
      console.error("No download URL found for material", material);
      return;
    }

    try {
      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error("Failed to fetch file");
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const urlPath = downloadUrl.split("?")[0];
      const extensionMatch = urlPath.match(/\.([a-zA-Z0-9]+)$/);
      const extension = extensionMatch?.[1]
        ? `.${extensionMatch[1]}`
        : materialType === "recorded"
          ? ".mp4"
          : ".pdf";

      link.href = blobUrl;
      link.download = `${title}${extension}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      // Fallback for CORS-restricted CDN URLs
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.download = title;
      document.body.appendChild(link);
      link.click();
      link.remove();
      console.error("Direct download failed, opened file in new tab", error);
    }
  };

  const handleRename = async () => {
    const pdfId = renameTarget?._id || renameTarget?.id || "";
    const nextTitle = renameValue.trim();

    if (!courseId || !pdfId || !nextTitle) {
      return;
    }

    try {
      await updatePdfTitle({
        courseId,
        pdfId,
        title: nextTitle,
      }).unwrap();
      setLocalMaterials((prev) =>
        prev.map((material) => {
          const materialId = material?._id || material?.id || "";
          return materialId === pdfId
            ? {
                ...material,
                title: nextTitle,
              }
            : material;
        }),
      );
      setRenameTarget((prev) =>
        prev
          ? {
              ...prev,
              title: nextTitle,
            }
          : prev,
      );
      handleCloseRename();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to rename pdf material", err);
    }
  };

  const headerLabel =
    materialType === "recorded" ? "Recorded Lectures" : "Pdf Materials";
  const isPdfMaterial = materialType !== "recorded";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 px-4 py-6">
      <div className="w-full max-w-[1040px] overflow-hidden rounded-[16px] bg-white">
        <div className="flex items-center justify-between border-b border-[#0000001A] px-6 py-5">
          <div className="flex flex-wrap items-center gap-4">
            <h4 className="text-[22px] font-semibold text-[#111827]">
              {item?.title || "Course Materials"}
            </h4>
            <span className="rounded-full bg-[#F3D6F1] px-4 py-1 text-[#923D8C]">
              {headerLabel}
            </span>
          </div>
          <button
            type="button"
            aria-label="Close materials modal"
            onClick={onClose}
            className="rounded-full p-1 text-[#111827] transition hover:bg-black/5"
          >
            <X size={22} />
          </button>
        </div>

        <div className="px-6 py-6">
          <div className="rounded-[12px] border border-dashed border-[#C7C7C7] p-4">
            {localMaterials.length === 0 ? (
              <div className="rounded-lg border border-dashed border-[#E5E7EB] px-4 py-6 text-center text-sm text-[#6B7280]">
                No materials available for this module.
              </div>
            ) : (
              <div className="space-y-3">
                {localMaterials.map((material, index) => {
                  const title = material?.title || `Material ${index + 1}`;
                  const materialId = material?._id || material?.id || "";
                  const isDeletingThisMaterial =
                    deletingMaterialId === materialId;
                  return (
                    <div
                      key={materialId || `${title}-${index}`}
                      className="flex flex-wrap items-center justify-between gap-4 rounded-lg bg-[#F0F0F0] px-4 py-3"
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-3">
                        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#F3D9F4] text-sm text-[#6B7280]">
                          {index + 1}
                        </span>
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#F3E8FF]">
                          {materialType === "recorded" ? (
                            <VideoTypeIcon />
                          ) : (
                            <PdfTypeIcon />
                          )}
                        </span>
                        <p className="truncate text-sm text-[#111827]">
                          {title}
                        </p>
                        {isPdfMaterial ? (
                          <button
                            type="button"
                            onClick={() => handleOpenRename(material)}
                            className="text-sm text-[#2563EB] underline"
                          >
                            Rename
                          </button>
                        ) : null}
                      </div>

                      <div className="flex items-center gap-4 text-[#6B7280]">
                        <button
                          type="button"
                          aria-label={`Download ${title}`}
                          onClick={() => handleDownload(material)}
                          disabled={isDeletingThisMaterial}
                          className="transition hover:text-[#4B5563]"
                        >
                          <Download size={18} />
                        </button>
                        <button
                          type="button"
                          aria-label={`Delete ${title}`}
                          onClick={() => handleDelete(material)}
                          disabled={isDeletingThisMaterial}
                          className="transition hover:text-[#4B5563] disabled:opacity-50"
                        >
                          {isDeletingThisMaterial ? (
                            <span className="block h-[18px] w-[18px] animate-spin rounded-full border-2 border-[#9CA3AF] border-t-transparent" />
                          ) : (
                            <Trash2 size={18} />
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {renameTarget ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/55 px-4 py-6">
          <div className="w-full max-w-[568px] overflow-hidden rounded-[16px] bg-white shadow-[0_20px_40px_rgba(0,0,0,0.18)]">
            <div className="flex items-center justify-between border-b border-[#E5E7EB] px-9 py-6">
              <h3 className="text-[17px] font-semibold text-[#1F2937]">
                Rename File
              </h3>
              <button
                type="button"
                aria-label="Close rename modal"
                onClick={handleCloseRename}
                className="rounded-full p-1 text-[#9CA3AF] transition hover:bg-black/5"
              >
                <X size={24} />
              </button>
            </div>

            <div className="border-b border-[#E5E7EB] px-9 py-10">
              <label className="block">
                <span className="text-[13px] font-semibold uppercase tracking-[0.04em] text-[#6B7280]">
                  New Filename
                </span>
                <input
                  type="text"
                  value={renameValue}
                  onChange={(event) => setRenameValue(event.target.value)}
                  className="mt-4 h-[54px] w-full rounded-xl border border-[#923D8C] px-5 text-[18px] text-[#111827] outline-none"
                />
              </label>
            </div>

            <div className="flex items-center justify-end gap-9 px-9 py-6">
              <button
                type="button"
                onClick={handleCloseRename}
                className="text-[18px] font-semibold text-[#4B5563]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRename}
                disabled={isUpdatingTitle || !renameValue.trim()}
                className="rounded-[10px] bg-[#923D8C] px-10 py-3 text-[18px] font-semibold text-white shadow-[0_4px_12px_rgba(146,61,140,0.28)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isUpdatingTitle ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CourseMaterialsModal;
